import produce from "immer";
import _mapValues from "lodash/mapValues";
import { TableSTORE } from "src/store/tableData/reducer/forTableId";
import { FlowioTableRow } from "src/components/pages/TableRowCreate/types";
import { getTableRowByTemplateId } from "src/requests/tableRowActions/requestCreators";
import { FlowioInputValue, TemplateId } from "../../globalTypes";
import {
  RowKey,
  TableCells,
  TableCellValue,
  TableColumnInputType,
  TableColumns,
  TableRow,
} from "../../requests/table/types";
import {
  EditRowWithColumns,
  TableRowValueDict,
} from "../../requests/tableRowActions/types";
import { TableIdentifier } from "../../store/tableData/types";
import { removeEmptyElements } from "../array";

export const makeGetValueFromRow =
  (row: TableRow) =>
  <RT extends TableCellValue>(field?: string | null) => {
    if (!field) return undefined;
    return row.cells?.[field]?.value as RT | undefined;
  };

export const getOnlyVisibleRowCells = (row: TableRow, columns: TableColumns) =>
  produce(row, (draft: TableRow) => {
    if (!draft || !columns) return;

    Object.keys(draft.cells).forEach((key) => {
      if (columns[key]?.inputType === "hidden") {
        delete draft.cells[key];
      }
    });
  });

export function flowioInputValueToTableCellValue(
  inputValue: FlowioInputValue
): TableCellValue {
  if (inputValue === undefined) return null;

  return inputValue as TableCellValue;
}

export const tableCells2valueDict = (cells?: TableCells): TableRowValueDict => {
  if (!cells) return {};
  return _mapValues(cells, (cell) => cell.value);
};

export const templateId2identifier = (
  templateId: TemplateId
): TableIdentifier => ({
  kind: "template",
  idWithinKind: templateId,
});

export const pickRowsByKey = (rows: TableRow[], keysToPick: RowKey[]) => {
  const pickRowByKey = (key: RowKey) => rows.find((r) => r.keyValue === key);

  return keysToPick
    .map(pickRowByKey) // I <3 PRETTIER
    .filter(removeEmptyElements);
};

const filterNotEditableColKeys = (
  colKey: string,
  columns: TableColumns
): boolean => {
  const { editable, isKey, inputType } = columns[colKey];
  return !editable || isKey || inputType === TableColumnInputType.Link;
};

const getCopyRowWithModifiedValues = (
  row: TableRow | undefined,
  columns: TableColumns | undefined
): TableRow | undefined => {
  if (!row || !columns) return undefined;

  const colKeysToNull = Object.keys(columns).filter((colKey) =>
    filterNotEditableColKeys(colKey, columns)
  );

  return produce(row, (draft) => {
    draft.keyValue = -1;
    Object.keys(columns).forEach((colKey) => {
      draft.cells[colKey].unformattedValue = null;
      draft.cells[colKey].unmodifiedValue = null;

      if (colKeysToNull.includes(colKey)) {
        draft.cells[colKey].value = null;
      }
    });
  });
};

export const getModifiedTableRowWithColumns = (
  editRow: FlowioTableRow
): FlowioTableRow => {
  if (!editRow) return null;

  const updatedRow = getCopyRowWithModifiedValues(editRow.row, editRow.columns);

  if (!updatedRow) return null;

  return { row: updatedRow, columns: editRow.columns };
};

export const getRowToCopyByRowHash = (
  rowHashToCopy: string,
  table: TableSTORE | null
): TableRow | undefined => {
  if (!table) return undefined;

  const { rowsByHash, columns } = table;

  const rowToCopy = getCopyRowWithModifiedValues(
    rowsByHash[rowHashToCopy],
    columns
  );

  return rowToCopy;
};

export const fetchEditTableRow = (
  templateId: number,
  rowKey: number | string
): Promise<EditRowWithColumns | null> =>
  getTableRowByTemplateId({
    rowKeyValue: Number(rowKey),
    templateId,
    editable: true,
  })
    .then(({ data }) => data)
    .catch(() => null);
